import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Autocomplete,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Step,
  Stepper,
  StepLabel,
  CircularProgress,
  FormHelperText,
  Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { Eye, EyeOff, Globe } from 'lucide-react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import ReactCountryFlag from "react-country-flag";
import ReactFlagsSelect from 'react-flags-select';
import { useNavigate } from 'react-router-dom';
import logo from '../../assets/logo1-removebg-preview.png';
import { api, BusinessSignupData, UserSignupData, Category } from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';

const currencies = [
  { code: 'USD', name: 'Dollar américain', sign: '$', country: 'US' },
  { code: 'EUR', name: 'Euro', sign: '€', country: 'EU' },
  { code: 'AOA', name: 'Kwanza angolais', sign: 'Kz', country: 'AO' },
  { code: 'GBP', name: 'Livre sterling', sign: '£', country: 'GB' },
  { code: 'JPY', name: 'Yen japonais', sign: '¥', country: 'JP' },
  { code: 'AUD', name: 'Dollar australien', sign: 'A$', country: 'AU' },
  { code: 'CAD', name: 'Dollar canadien', sign: 'C$', country: 'CA' },
  { code: 'CHF', name: 'Franc suisse', sign: 'CHF', country: 'CH' },
  { code: 'CNY', name: 'Yuan chinois', sign: '¥', country: 'CN' },
  { code: 'HKD', name: 'Dollar de Hong Kong', sign: 'HK$', country: 'HK' },
  { code: 'NZD', name: 'Dollar néo-zélandais', sign: 'NZ$', country: 'NZ' },
  { code: 'SEK', name: 'Couronne suédoise', sign: 'kr', country: 'SE' },
  { code: 'KRW', name: 'Won sud-coréen', sign: '₩', country: 'KR' },
  { code: 'SGD', name: 'Dollar de Singapour', sign: 'S$', country: 'SG' },
  { code: 'NOK', name: 'Couronne norvégienne', sign: 'kr', country: 'NO' },
  { code: 'MXN', name: 'Peso mexicain', sign: '$', country: 'MX' },
  { code: 'INR', name: 'Roupie indienne', sign: '₹', country: 'IN' },
  { code: 'RUB', name: 'Rouble russe', sign: '₽', country: 'RU' },
  { code: 'ZAR', name: 'Rand sud-africain', sign: 'R', country: 'ZA' },
  { code: 'TRY', name: 'Livre turque', sign: '₺', country: 'TR' },
  { code: 'BRL', name: 'Real brésilien', sign: 'R$', country: 'BR' },
  { code: 'TWD', name: 'Dollar taïwanais', sign: 'NT$', country: 'TW' },
  { code: 'DKK', name: 'Couronne danoise', sign: 'kr', country: 'DK' },
  { code: 'PLN', name: 'Złoty polonais', sign: 'zł', country: 'PL' },
  { code: 'THB', name: 'Baht thaïlandais', sign: '฿', country: 'TH' },
  { code: 'IDR', name: 'Roupie indonésienne', sign: 'Rp', country: 'ID' },
  { code: 'HUF', name: 'Forint hongrois', sign: 'Ft', country: 'HU' },
  { code: 'CZK', name: 'Couronne tchèque', sign: 'Kč', country: 'CZ' },
  { code: 'ILS', name: 'Shekel israélien', sign: '₪', country: 'IL' },
  { code: 'CLP', name: 'Peso chilien', sign: '$', country: 'CL' },
  { code: 'PHP', name: 'Peso philippin', sign: '₱', country: 'PH' },
  { code: 'AED', name: 'Dirham des Émirats arabes unis', sign: 'د.إ', country: 'AE' },
  { code: 'COP', name: 'Peso colombien', sign: '$', country: 'CO' },
  { code: 'SAR', name: 'Riyal saoudien', sign: '﷼', country: 'SA' },
  { code: 'MYR', name: 'Ringgit malaisien', sign: 'RM', country: 'MY' },
  { code: 'RON', name: 'Leu roumain', sign: 'lei', country: 'RO' },
  { code: 'CDF', name: 'Franc congolais', sign: 'FC', country: 'CD' },
  { code: 'ARS', name: 'Peso argentin', sign: '$', country: 'AR' },
  { code: 'BGN', name: 'Lev bulgare', sign: 'лв', country: 'BG' },
  { code: 'DZD', name: 'Dinar algérien', sign: 'د.ج', country: 'DZ' },
  { code: 'BAM', name: 'Mark convertible', sign: 'KM', country: 'BA' },
  { code: 'EGP', name: 'Livre égyptienne', sign: 'E£', country: 'EG' },
  { code: 'GHS', name: 'Cedi ghanéen', sign: '₵', country: 'GH' },
  { code: 'HRK', name: 'Kuna croate', sign: 'kn', country: 'HR' },
  { code: 'ISK', name: 'Couronne islandaise', sign: 'kr', country: 'IS' },
  { code: 'JOD', name: 'Dinar jordanien', sign: 'د.ا', country: 'JO' },
  { code: 'KES', name: 'Shilling kényan', sign: 'KSh', country: 'KE' },
  { code: 'KWD', name: 'Dinar koweïtien', sign: 'د.ك', country: 'KW' },
  { code: 'LBP', name: 'Livre libanaise', sign: 'ل.ل', country: 'LB' },
  { code: 'MAD', name: 'Dirham marocain', sign: 'د.م.', country: 'MA' },
  { code: 'NGN', name: 'Naira nigérian', sign: '₦', country: 'NG' },
  { code: 'OMR', name: 'Rial omanais', sign: 'ر.ع.', country: 'OM' },
  { code: 'PEN', name: 'Sol péruvien', sign: 'S/', country: 'PE' },
  { code: 'PKR', name: 'Roupie pakistanaise', sign: '₨', country: 'PK' },
  { code: 'QAR', name: 'Riyal qatari', sign: 'ر.ق', country: 'QA' },
  { code: 'RSD', name: 'Dinar serbe', sign: 'дин.', country: 'RS' },
  { code: 'TND', name: 'Dinar tunisien', sign: 'د.ت', country: 'TN' },
  { code: 'UAH', name: 'Hryvnia ukrainienne', sign: '₴', country: 'UA' },
  { code: 'UYU', name: 'Peso uruguayen', sign: '$U', country: 'UY' },
  { code: 'VND', name: 'Đồng vietnamien', sign: '₫', country: 'VN' }
];

const languages = [
  { value: 'fr', label: 'Français' },
  { value: 'en', label: 'English' },
];

const countries = [
  { code: 'AF', name: 'Afghanistan' },
  { code: 'ZA', name: 'Afrique du Sud' },
  { code: 'AL', name: 'Albanie' },
  { code: 'DZ', name: 'Algérie' },
  { code: 'DE', name: 'Allemagne' },
  { code: 'AD', name: 'Andorre' },
  { code: 'AO', name: 'Angola' },
  { code: 'AG', name: 'Antigua-et-Barbuda' },
  { code: 'SA', name: 'Arabie Saoudite' },
  { code: 'AR', name: 'Argentine' },
  { code: 'AM', name: 'Arménie' },
  { code: 'AU', name: 'Australie' },
  { code: 'AT', name: 'Autriche' },
  { code: 'AZ', name: 'Azerbaïdjan' },
  { code: 'BS', name: 'Bahamas' },
  { code: 'BH', name: 'Bahreïn' },
  { code: 'BD', name: 'Bangladesh' },
  { code: 'BB', name: 'Barbade' },
  { code: 'BE', name: 'Belgique' },
  { code: 'BZ', name: 'Belize' },
  { code: 'BJ', name: 'Bénin' },
  { code: 'BT', name: 'Bhoutan' },
  { code: 'BY', name: 'Biélorussie' },
  { code: 'MM', name: 'Birmanie' },
  { code: 'BO', name: 'Bolivie' },
  { code: 'BA', name: 'Bosnie-Herzégovine' },
  { code: 'BW', name: 'Botswana' },
  { code: 'BR', name: 'Brésil' },
  { code: 'BN', name: 'Brunei' },
  { code: 'BG', name: 'Bulgarie' },
  { code: 'BF', name: 'Burkina Faso' },
  { code: 'BI', name: 'Burundi' },
  { code: 'KH', name: 'Cambodge' },
  { code: 'CM', name: 'Cameroun' },
  { code: 'CA', name: 'Canada' },
  { code: 'CV', name: 'Cap-Vert' },
  { code: 'CF', name: 'République centrafricaine' },
  { code: 'CL', name: 'Chili' },
  { code: 'CN', name: 'Chine' },
  { code: 'CY', name: 'Chypre' },
  { code: 'CO', name: 'Colombie' },
  { code: 'KM', name: 'Comores' },
  { code: 'CG', name: 'République du Congo' },
  { code: 'CD', name: 'République démocratique du Congo' },
  { code: 'KP', name: 'Corée du Nord' },
  { code: 'KR', name: 'Corée du Sud' },
  { code: 'CR', name: 'Costa Rica' },
  { code: 'CI', name: 'Côte d\'Ivoire' },
  { code: 'HR', name: 'Croatie' },
  { code: 'CU', name: 'Cuba' },
  { code: 'DK', name: 'Danemark' },
  { code: 'DJ', name: 'Djibouti' },
  { code: 'DM', name: 'Dominique' },
  { code: 'EG', name: 'Égypte' },
  { code: 'AE', name: 'Émirats arabes unis' },
  { code: 'EC', name: 'Équateur' },
  { code: 'ER', name: 'Érythrée' },
  { code: 'ES', name: 'Espagne' },
  { code: 'EE', name: 'Estonie' },
  { code: 'US', name: 'États-Unis' },
  { code: 'ET', name: 'Éthiopie' },
  { code: 'FJ', name: 'Fidji' },
  { code: 'FI', name: 'Finlande' },
  { code: 'FR', name: 'France' },
  { code: 'GA', name: 'Gabon' },
  { code: 'GM', name: 'Gambie' },
  { code: 'GE', name: 'Géorgie' },
  { code: 'GH', name: 'Ghana' },
  { code: 'GR', name: 'Grèce' },
  { code: 'GD', name: 'Grenade' },
  { code: 'GT', name: 'Guatemala' },
  { code: 'GN', name: 'Guinée' },
  { code: 'GQ', name: 'Guinée équatoriale' },
  { code: 'GW', name: 'Guinée-Bissau' },
  { code: 'GY', name: 'Guyana' },
  { code: 'HT', name: 'Haïti' },
  { code: 'HN', name: 'Honduras' },
  { code: 'HU', name: 'Hongrie' },
  { code: 'IN', name: 'Inde' },
  { code: 'ID', name: 'Indonésie' },
  { code: 'IQ', name: 'Irak' },
  { code: 'IR', name: 'Iran' },
  { code: 'IE', name: 'Irlande' },
  { code: 'IS', name: 'Islande' },
  { code: 'IL', name: 'Israël' },
  { code: 'IT', name: 'Italie' },
  { code: 'JM', name: 'Jamaïque' },
  { code: 'JP', name: 'Japon' },
  { code: 'JO', name: 'Jordanie' },
  { code: 'KZ', name: 'Kazakhstan' },
  { code: 'KE', name: 'Kenya' },
  { code: 'KG', name: 'Kirghizistan' },
  { code: 'KI', name: 'Kiribati' },
  { code: 'KW', name: 'Koweït' },
  { code: 'LA', name: 'Laos' },
  { code: 'LS', name: 'Lesotho' },
  { code: 'LV', name: 'Lettonie' },
  { code: 'LB', name: 'Liban' },
  { code: 'LR', name: 'Libéria' },
  { code: 'LY', name: 'Libye' },
  { code: 'LI', name: 'Liechtenstein' },
  { code: 'LT', name: 'Lituanie' },
  { code: 'LU', name: 'Luxembourg' },
  { code: 'MK', name: 'Macédoine du Nord' },
  { code: 'MG', name: 'Madagascar' },
  { code: 'MY', name: 'Malaisie' },
  { code: 'MW', name: 'Malawi' },
  { code: 'MV', name: 'Maldives' },
  { code: 'ML', name: 'Mali' },
  { code: 'MT', name: 'Malte' },
  { code: 'MA', name: 'Maroc' },
  { code: 'MH', name: 'Îles Marshall' },
  { code: 'MU', name: 'Maurice' },
  { code: 'MR', name: 'Mauritanie' },
  { code: 'MX', name: 'Mexique' },
  { code: 'FM', name: 'Micronésie' },
  { code: 'MD', name: 'Moldavie' },
  { code: 'MC', name: 'Monaco' },
  { code: 'MN', name: 'Mongolie' },
  { code: 'ME', name: 'Monténégro' },
  { code: 'MZ', name: 'Mozambique' },
  { code: 'NA', name: 'Namibie' },
  { code: 'NR', name: 'Nauru' },
  { code: 'NP', name: 'Népal' },
  { code: 'NI', name: 'Nicaragua' },
  { code: 'NE', name: 'Niger' },
  { code: 'NG', name: 'Nigeria' },
  { code: 'NO', name: 'Norvège' },
  { code: 'NZ', name: 'Nouvelle-Zélande' },
  { code: 'OM', name: 'Oman' },
  { code: 'UG', name: 'Ouganda' },
  { code: 'UZ', name: 'Ouzbékistan' },
  { code: 'PK', name: 'Pakistan' },
  { code: 'PW', name: 'Palaos' },
  { code: 'PS', name: 'Palestine' },
  { code: 'PA', name: 'Panama' },
  { code: 'PG', name: 'Papouasie-Nouvelle-Guinée' },
  { code: 'PY', name: 'Paraguay' },
  { code: 'NL', name: 'Pays-Bas' },
  { code: 'PE', name: 'Pérou' },
  { code: 'PH', name: 'Philippines' },
  { code: 'PL', name: 'Pologne' },
  { code: 'PT', name: 'Portugal' },
  { code: 'QA', name: 'Qatar' },
  { code: 'RO', name: 'Roumanie' },
  { code: 'GB', name: 'Royaume-Uni' },
  { code: 'RU', name: 'Russie' },
  { code: 'RW', name: 'Rwanda' },
  { code: 'KN', name: 'Saint-Kitts-et-Nevis' },
  { code: 'SM', name: 'Saint-Marin' },
  { code: 'VC', name: 'Saint-Vincent-et-les-Grenadines' },
  { code: 'LC', name: 'Sainte-Lucie' },
  { code: 'SB', name: 'Îles Salomon' },
  { code: 'SV', name: 'Salvador' },
  { code: 'WS', name: 'Samoa' },
  { code: 'ST', name: 'Sao Tomé-et-Principe' },
  { code: 'SN', name: 'Sénégal' },
  { code: 'RS', name: 'Serbie' },
  { code: 'SC', name: 'Seychelles' },
  { code: 'SL', name: 'Sierra Leone' },
  { code: 'SG', name: 'Singapour' },
  { code: 'SK', name: 'Slovaquie' },
  { code: 'SI', name: 'Slovénie' },
  { code: 'SO', name: 'Somalie' },
  { code: 'SD', name: 'Soudan' },
  { code: 'SS', name: 'Soudan du Sud' },
  { code: 'LK', name: 'Sri Lanka' },
  { code: 'SE', name: 'Suède' },
  { code: 'CH', name: 'Suisse' },
  { code: 'SR', name: 'Suriname' },
  { code: 'SZ', name: 'Eswatini' },
  { code: 'SY', name: 'Syrie' },
  { code: 'TJ', name: 'Tadjikistan' },
  { code: 'TZ', name: 'Tanzanie' },
  { code: 'TD', name: 'Tchad' },
  { code: 'CZ', name: 'République tchèque' },
  { code: 'TH', name: 'Thaïlande' },
  { code: 'TL', name: 'Timor oriental' },
  { code: 'TG', name: 'Togo' },
  { code: 'TO', name: 'Tonga' },
  { code: 'TT', name: 'Trinité-et-Tobago' },
  { code: 'TN', name: 'Tunisie' },
  { code: 'TM', name: 'Turkménistan' },
  { code: 'TR', name: 'Turquie' },
  { code: 'TV', name: 'Tuvalu' },
  { code: 'UA', name: 'Ukraine' },
  { code: 'UY', name: 'Uruguay' },
  { code: 'VU', name: 'Vanuatu' },
  { code: 'VA', name: 'Vatican' },
  { code: 'VE', name: 'Venezuela' },
  { code: 'VN', name: 'Viêt Nam' },
  { code: 'YE', name: 'Yémen' },
  { code: 'ZM', name: 'Zambie' },
  { code: 'ZW', name: 'Zimbabwe' }
];

export const Register = () => {
  const navigate = useNavigate();
  const { language, setLanguage } = useLanguage();
  const [activeStep, setActiveStep] = useState(0);
  const [formErrors, setFormErrors] = useState<{[key: string]: string}>({});
  const [businessData, setBusinessData] = useState<BusinessSignupData>({
    name: '',
    address: '',
    currency: '',
    signCurrency: '',
    lang: '',
    country: '',
    category: ''
  });
  const [userData, setUserData] = useState<UserSignupData>({
    username: '',
    password: '',
    phone: '',
    role: 'Admin',
    email: '',
    companyId: '',
  });
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true); 
  const [error, setError] = useState<string | null>(null);
  const [currencySearch, setCurrencySearch] = useState('');
  const [categorySearch, setCategorySearch] = useState('');
  const [countrySearch, setCountrySearch] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [phonePrefix, setPhonePrefix] = useState('+33'); // France par défaut
  const [phoneSearch, setPhoneSearch] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('FR');

  const countryOptions = [
    { code: 'FR', name: 'France', prefix: '+33' },
    { code: 'BE', name: 'Belgique', prefix: '+32' },
    { code: 'CH', name: 'Suisse', prefix: '+41' },
    { code: 'CA', name: 'Canada', prefix: '+1' },
    { code: 'US', name: 'États-Unis', prefix: '+1' },
    { code: 'UK', name: 'Royaume-Uni', prefix: '+44' },
    { code: 'DE', name: 'Allemagne', prefix: '+49' },
    { code: 'IT', name: 'Italie', prefix: '+39' },
    { code: 'ES', name: 'Espagne', prefix: '+34' },
    { code: 'PT', name: 'Portugal', prefix: '+351' },
    { code: 'MA', name: 'Maroc', prefix: '+212' },
    { code: 'DZ', name: 'Algérie', prefix: '+213' },
    { code: 'TN', name: 'Tunisie', prefix: '+216' },
    { code: 'SN', name: 'Sénégal', prefix: '+221' },
    { code: 'CI', name: 'Côte d\'Ivoire', prefix: '+225' },
    { code: 'CM', name: 'Cameroun', prefix: '+237' },
    { code: 'CD', name: 'RD Congo', prefix: '+243' },
  ];

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        console.log('Starting to fetch categories...');
        const response = await api.getCategories();
        console.log('Categories received:', response);
        
        if (Array.isArray(response) && response.length > 0) {
          setCategories(response);
          setError(null);
        } else {
          console.error('Invalid categories data:', response);
          setError('Aucune catégorie disponible');
          setCategories([]);
        }
      } catch (err) {
        console.error('Error in component while fetching categories:', err);
        setError(err instanceof Error ? err.message : 'Erreur lors du chargement des catégories');
        setCategories([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  // Synchroniser la langue avec businessData
  useEffect(() => {
    if (businessData.lang) {
      setLanguage(businessData.lang as 'en' | 'fr');
    }
  }, [businessData.lang, setLanguage]);

  // Traductions locales
  const text = {
    fr: {
      createAccount: 'Créer un compte',
      companyInfo: 'Informations de l\'entreprise',
      userInfo: 'Informations de l\'utilisateur',
      companyName: 'Nom de l\'entreprise',
      address: 'Adresse',
      currency: 'Devise',
      language: 'Langue',
      country: 'Pays',
      category: 'Catégorie',
      continue: 'Continuer',
      username: 'Nom d\'utilisateur',
      email: 'Email',
      password: 'Mot de passe',
      passwordHelp: 'Le mot de passe doit contenir au moins 8 caractères',
      phone: 'Téléphone',
      back: 'Retour',
      haveAccount: 'Vous avez déjà un compte ?',
      login: 'Connectez-vous',
      next: 'Suivant',
      createUser: 'Créer le compte',
      // Messages d'erreur
      required: 'Ce champ est obligatoire',
      invalidEmail: 'Veuillez entrer une adresse email valide',
      invalidPassword: 'Le mot de passe doit contenir au moins 8 caractères',
      invalidPhone: 'Veuillez entrer un numéro de téléphone valide',
      register: 'Inscription',
      searchCountry: 'Rechercher un pays',
      selectCountry: 'Sélectionner un pays',
      phoneNumber: 'Numéro de téléphone',
    },
    en: {
      createAccount: 'Create Account',
      companyInfo: 'Company Information',
      userInfo: 'User Information',
      companyName: 'Company Name',
      address: 'Address',
      currency: 'Currency',
      language: 'Language',
      country: 'Country',
      category: 'Category',
      continue: 'Continue',
      username: 'Username',
      email: 'Email',
      password: 'Password',
      passwordHelp: 'Password must be at least 8 characters long',
      phone: 'Phone',
      back: 'Back',
      haveAccount: 'Already have an account?',
      login: 'Login',
      next: 'Next',
      createUser: 'Create Account',
      // Messages d'erreur
      required: 'This field is required',
      invalidEmail: 'Please enter a valid email address',
      invalidPassword: 'Password must be at least 8 characters long',
      invalidPhone: 'Please enter a valid phone number',
      register: 'Register',
      searchCountry: 'Search country',
      selectCountry: 'Select country',
      phoneNumber: 'Phone number',
    }
  };

  const currentText = text[language as keyof typeof text];

  const steps = [currentText.companyInfo, currentText.userInfo];

  const validateForm = () => {
    const errors: {[key: string]: string} = {};
    
    if (activeStep === 0) {
      if (!businessData.name.trim()) errors.name = currentText.required;
      if (!businessData.address.trim()) errors.address = currentText.required;
      if (!businessData.currency) errors.currency = currentText.required;
      if (!businessData.lang) errors.lang = currentText.required;
      if (!businessData.country) errors.country = currentText.required;
      if (!businessData.category) errors.category = currentText.required;
    } else {
      if (!userData.username.trim()) errors.username = currentText.required;
      if (!userData.email.trim()) {
        errors.email = currentText.required;
      } else if (!/\S+@\S+\.\S+/.test(userData.email)) {
        errors.email = currentText.invalidEmail;
      }
      if (!userData.password) {
        errors.password = currentText.required;
      } else if (userData.password.length < 8) {
        errors.password = currentText.invalidPassword;
      }
      if (!userData.phone) errors.phone = currentText.required;
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      if (activeStep === 0) {
        const response = await api.signupBusiness(businessData);
        console.log('Réponse création entreprise:', response);
        
        // Extraction de l'ID de l'entreprise de la réponse
        const companyId = response?.company?._id;
        
        if (companyId) {
          console.log('ID entreprise récupéré:', companyId);
          setUserData(prev => ({
            ...prev,
            companyId: companyId
          }));
          setActiveStep(1);
        } else {
          console.error('ID entreprise non trouvé dans la réponse:', response);
          setError('Erreur: ID entreprise non trouvé');
        }
      } else {
        // Validation des données utilisateur
        if (!userData.username || !userData.password || !userData.email || !userData.phone || !userData.companyId) {
          console.error('Données utilisateur invalides:', userData);
          setError('Veuillez remplir tous les champs obligatoires');
          return;
        }

        console.log('Données utilisateur avant création:', userData);
        try {
          const response = await api.createUser({
            ...userData,
            role: 'Admin' // S'assurer que le rôle est bien défini
          });
          console.log('Réponse création utilisateur:', response);
          
          // Si la réponse contient un message de succès ou un utilisateur créé
          if (response.message?.toLowerCase().includes('success') || response.user || response.data) {
            console.log('Création utilisateur réussie');
            navigate('/login');
          } else {
            console.error('Erreur création utilisateur:', response);
            setError(response.message || 'Une erreur est survenue lors de la création du compte');
          }
        } catch (error: any) {
          console.error('Erreur création utilisateur:', error);
          setError(error.message || 'Une erreur est survenue lors de la création du compte');
        }
      }
    } catch (err: any) {
      setError(err.message || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  // Filtrer les devises en fonction de la recherche
  const filteredCurrencies = currencies.filter(currency => 
    currency.code.toLowerCase().includes(currencySearch.toLowerCase()) ||
    currency.name.toLowerCase().includes(currencySearch.toLowerCase())
  );

  // Filtrer les catégories en fonction de la recherche
  const filteredCategories = useMemo(() => {
    if (!categorySearch.trim()) return categories;
    
    const searchTerm = categorySearch.toLowerCase().trim();
    return categories.filter(category => {
      const nameMatch = category.name?.toLowerCase().includes(searchTerm);
      const englishNameMatch = category.nameEnglish?.toLowerCase().includes(searchTerm);
      return nameMatch || englishNameMatch;
    });
  }, [categories, categorySearch]);

  // Filtrer les pays en fonction de la recherche
  const filteredCountries = useMemo(() => {
    if (!countrySearch) return countries;
    const searchTerm = countrySearch.toLowerCase().trim();
    return countries.filter(country => 
      country.name.toLowerCase().includes(searchTerm)
    ).sort((a, b) => {
      // Mettre en premier les pays qui commencent par le terme de recherche
      const aStartsWith = a.name.toLowerCase().startsWith(searchTerm);
      const bStartsWith = b.name.toLowerCase().startsWith(searchTerm);
      if (aStartsWith && !bStartsWith) return -1;
      if (!aStartsWith && bStartsWith) return 1;
      return a.name.localeCompare(b.name);
    });
  }, [countrySearch]);

  return (
    <Box 
      sx={{ 
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        backgroundColor: '#f8fafc',
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Box sx={{ 
          mb: 4, 
          textAlign: 'center', 
          position: 'relative'
        }}>
          <img 
            src={logo} 
            alt="Logo" 
            style={{ 
              height: '60px',
              marginBottom: '1rem',
              cursor: 'pointer'
            }}
            onClick={() => navigate('/')}
          />
          {/* Sélecteur de langue */}
          <FormControl 
            sx={{ 
              position: 'absolute',
              right: 0,
              top: 0
            }}
          >
            <Select
              value={language}
              onChange={(e) => {
                const newLang = e.target.value as 'fr' | 'en';
                setLanguage(newLang);
                setBusinessData(prev => ({ ...prev, lang: newLang }));
              }}
              size="small"
              sx={{
                '& .MuiSelect-select': {
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  pr: 4,
                },
              }}
            >
              <MenuItem value="fr" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Globe size={16} /> FR
              </MenuItem>
              <MenuItem value="en" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Globe size={16} /> EN
              </MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Paper 
          elevation={0}
          sx={{ 
            p: 4,
            borderRadius: '16px',
            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
          }}
        >
          <Typography 
            variant="h5" 
            sx={{ 
              mb: 3,
              fontWeight: 'bold',
              color: '#1a2027',
              textAlign: 'center'
            }}
          >
            {currentText.createAccount}
          </Typography>

          <Typography variant="h4" sx={{ 
            textAlign: 'center', 
            mb: 4,
            color: '#0D3C4C',
            fontWeight: 600
          }}>
            {activeStep === 0 ? currentText.companyInfo : currentText.userInfo}
          </Typography>

          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label, index) => {
              const stepProps: { completed?: boolean } = {};
              const labelProps: {
                optional?: React.ReactNode;
              } = {};
              return (
                <Step key={label} {...stepProps}>
                  <StepLabel {...labelProps} sx={{
                    '& .MuiStepLabel-root .Mui-completed': {
                      color: '#0D3C4C', 
                    },
                    '& .MuiStepLabel-root .Mui-active': {
                      color: '#0D3C4C',
                    },
                    '& .MuiStepLabel-root .MuiStepIcon-root': {
                      color: '#E0E0E0',
                    },
                    '& .MuiStepLabel-root .MuiStepIcon-text': {
                      fill: 'white',
                    },
                  }}>{label}</StepLabel>
                </Step>
              );
            })}
          </Stepper>

          {error && (
            <Alert 
              severity="error"
              sx={{ 
                mb: 3,
                borderRadius: '8px',
                '& .MuiAlert-icon': {
                  color: '#d32f2f'
                },
                '& .MuiAlert-message': {
                  color: '#d32f2f',
                  fontWeight: 500
                },
                bgcolor: '#fdeded',
                border: '1px solid #ef5350',
                display: 'flex',
                alignItems: 'center'
              }}
              action={
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  onClick={() => setError('')}
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              }
            >
              {error}
            </Alert>
          )}

          {activeStep === 0 ? (
            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label={currentText.companyName}
                value={businessData.name}
                onChange={(e) => {
                  setBusinessData(prev => ({ ...prev, name: e.target.value }));
                  if (formErrors.name) {
                    setFormErrors(prev => ({ ...prev, name: '' }));
                  }
                }}
                error={!!formErrors.name}
                helperText={formErrors.name}
                sx={{ mb: 3 }}
              />

              <TextField
                fullWidth
                label={currentText.address}
                value={businessData.address}
                onChange={(e) => {
                  setBusinessData(prev => ({ ...prev, address: e.target.value }));
                  if (formErrors.address) {
                    setFormErrors(prev => ({ ...prev, address: '' }));
                  }
                }}
                error={!!formErrors.address}
                helperText={formErrors.address}
                sx={{ mb: 3 }}
              />

              <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                {currentText.currency}
              </Typography>
              <FormControl fullWidth error={!!formErrors.currency} sx={{ mb: 3 }}>
                <Autocomplete
                  value={currencies.find(c => c.code === businessData.currency) || null}
                  onChange={(e, newValue) => {
                    if (newValue) {
                      const currency = newValue.code;
                      const sign = newValue.sign;
                      setBusinessData(prev => ({ 
                        ...prev, 
                        currency,
                        signCurrency: sign
                      }));
                      if (formErrors.currency) {
                        setFormErrors(prev => ({ ...prev, currency: '' }));
                      }
                    }
                  }}
                  options={currencies}
                  getOptionLabel={(option) => `${option.name} (${option.sign})`}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={!!formErrors.currency}
                      helperText={formErrors.currency}
                      placeholder="Rechercher une devise..."
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <SearchIcon />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        )
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <MenuItem {...props}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ 
                          minWidth: 30,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <ReactCountryFlag
                            countryCode={option.country}
                            svg
                            style={{
                              width: '1.2em',
                              height: '1.2em',
                              borderRadius: '2px',
                              objectFit: 'cover'
                            }}
                          />
                        </Box>
                        <Box sx={{ 
                          minWidth: 30,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '1.2rem'
                        }}>
                          {option.sign}
                        </Box>
                        <Typography>
                          {option.name}
                        </Typography>
                      </Box>
                    </MenuItem>
                  )}
                />
              </FormControl>

              <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                {currentText.language}
              </Typography>
              <FormControl fullWidth error={!!formErrors.lang} sx={{ mb: 3 }}>
                <Select
                  value={businessData.lang}
                  onChange={(e) => {
                    const newLang = e.target.value;
                    setBusinessData(prev => ({ ...prev, lang: newLang }));
                    setLanguage(newLang as 'en' | 'fr');
                    if (formErrors.lang) {
                      setFormErrors(prev => ({ ...prev, lang: '' }));
                    }
                  }}
                >
                  <MenuItem value="fr">Français</MenuItem>
                  <MenuItem value="en">English</MenuItem>
                </Select>
                {formErrors.lang && (
                  <FormHelperText>{formErrors.lang}</FormHelperText>
                )}
              </FormControl>

              <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                {currentText.country}
              </Typography>
              <FormControl fullWidth error={!!formErrors.country} sx={{ mb: 3 }}>
                <Autocomplete
                  value={countries.find(c => c.code === businessData.country) || null}
                  onChange={(e, newValue) => {
                    if (newValue) {
                      setBusinessData(prev => ({ ...prev, country: newValue.code }));
                      if (formErrors.country) {
                        setFormErrors(prev => ({ ...prev, country: '' }));
                      }
                    }
                  }}
                  options={countries}
                  getOptionLabel={(option) => option.name}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={!!formErrors.country}
                      helperText={formErrors.country}
                      placeholder="Rechercher un pays..."
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <SearchIcon />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        )
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <MenuItem {...props}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ 
                          minWidth: 30,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <ReactCountryFlag
                            countryCode={option.code}
                            svg
                            style={{
                              width: '1.2em',
                              height: '1.2em',
                              borderRadius: '2px',
                              objectFit: 'cover'
                            }}
                          />
                        </Box>
                        <Typography>
                          {option.name}
                        </Typography>
                      </Box>
                    </MenuItem>
                  )}
                />
              </FormControl>

              <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                {currentText.category}
              </Typography>
              <FormControl fullWidth margin="normal" error={!!formErrors.category}>
                <InputLabel id="category-label">Catégorie</InputLabel>
                <Select
                  labelId="category-label"
                  id="category"
                  value={businessData.category || ''}
                  onChange={(e) => {
                    setBusinessData(prev => ({ ...prev, category: e.target.value }));
                    if (formErrors.category) {
                      setFormErrors(prev => ({ ...prev, category: '' }));
                    }
                  }}
                  label="Catégorie"
                >
                  {loading ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} />
                      Chargement des catégories...
                    </MenuItem>
                  ) : error ? (
                    <MenuItem disabled>
                      <Alert severity="error" style={{ width: '100%' }}>
                        {error}
                      </Alert>
                    </MenuItem>
                  ) : categories.length === 0 ? (
                    <MenuItem disabled>
                      Aucune catégorie disponible
                    </MenuItem>
                  ) : (
                    categories.map((category) => (
                      <MenuItem key={category._id} value={category._id}>
                        {language === 'fr' ? category.name : category.nameEnglish}
                      </MenuItem>
                    ))
                  )}
                </Select>
                {formErrors.category && (
                  <FormHelperText>{formErrors.category}</FormHelperText>
                )}
              </FormControl>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{
                  bgcolor: '#0D3C4C',
                  color: 'white',
                  py: 1.5,
                  '&:hover': {
                    bgcolor: '#0D3C4C',
                  },
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : currentText.continue}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label={currentText.username}
                value={userData.username}
                onChange={(e) => {
                  setUserData(prev => ({ ...prev, username: e.target.value }));
                  if (formErrors.username) {
                    setFormErrors(prev => ({ ...prev, username: '' }));
                  }
                }}
                error={!!formErrors.username}
                helperText={formErrors.username}
                sx={{ mb: 3 }}
              />

              <TextField
                fullWidth
                type="email"
                label={currentText.email}
                value={userData.email}
                onChange={(e) => {
                  setUserData(prev => ({ ...prev, email: e.target.value }));
                  if (formErrors.email) {
                    setFormErrors(prev => ({ ...prev, email: '' }));
                  }
                }}
                error={!!formErrors.email}
                helperText={formErrors.email}
                sx={{ mb: 3 }}
              />

              <FormControl fullWidth error={!!formErrors.password} sx={{ mb: 3 }}>
                <TextField
                  label={currentText.password}
                  type={showPassword ? 'text' : 'password'}
                  value={userData.password}
                  onChange={(e) => {
                    setUserData(prev => ({ ...prev, password: e.target.value }));
                    if (formErrors.password) {
                      setFormErrors(prev => ({ ...prev, password: '' }));
                    }
                  }}
                  error={!!formErrors.password}
                  helperText={formErrors.password}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <EyeOff /> : <Eye />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </FormControl>

              <Typography sx={{ mb: 1, color: 'text.secondary' }}>
                {currentText.phone}
              </Typography>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Box sx={{ width: '150px' }}>
                    <ReactFlagsSelect
                      selected={selectedCountry}
                      onSelect={(code) => {
                        setSelectedCountry(code);
                        const country = countryOptions.find(c => c.code === code);
                        if (country) {
                          setPhonePrefix(country.prefix);
                        }
                      }}
                      countries={countryOptions.map(c => c.code)}
                      customLabels={{
                        FR: "France",
                        BE: "Belgique",
                        CH: "Suisse",
                        CA: "Canada",
                        US: "États-Unis",
                        UK: "Royaume-Uni",
                        DE: "Allemagne",
                        IT: "Italie",
                        ES: "Espagne",
                        PT: "Portugal",
                        MA: "Maroc",
                        DZ: "Algérie",
                        TN: "Tunisie",
                        SN: "Sénégal",
                        CI: "Côte d'Ivoire",
                        CM: "Cameroun",
                        CD: "RD Congo"
                      }}
                      placeholder={currentText.selectCountry}
                      searchable
                      searchPlaceholder={currentText.searchCountry}
                    />
                  </Box>
                  <TextField
                    fullWidth
                    error={!!formErrors.phone}
                    helperText={formErrors.phone}
                    value={userData.phone.replace(phonePrefix, '')}
                    onChange={(e) => {
                      const phoneNumber = e.target.value.replace(/[^0-9]/g, '');
                      setUserData(prev => ({ ...prev, phone: phonePrefix + phoneNumber }));
                      if (formErrors.phone) {
                        setFormErrors(prev => ({ ...prev, phone: '' }));
                      }
                    }}
                    placeholder={currentText.phoneNumber}
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          {phonePrefix}
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => setActiveStep(0)}
                  sx={{
                    flex: 1,
                    height: '48px',
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: 500,
                    borderColor: '#0D3C4C',
                    color: '#0D3C4C',
                    '&:hover': {
                      borderColor: '#164B61',
                      backgroundColor: 'transparent',
                    },
                  }}
                >
                  {currentText.back}
                </Button>

                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  sx={{
                    flex: 1,
                    backgroundColor: '#0D3C4C',
                    height: '48px',
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: 500,
                    '&:hover': {
                      backgroundColor: '#164B61',
                    },
                  }}
                >
                  {loading ? <CircularProgress size={24} /> : currentText.createUser}
                </Button>
              </Box>
            </form>
          )}

          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {currentText.haveAccount}{' '}
              <Button
                onClick={() => navigate('/login')}
                sx={{
                  textTransform: 'none',
                  color: '#0D3C4C',
                  '&:hover': {
                    backgroundColor: 'transparent',
                    textDecoration: 'underline',
                  },
                }}
              >
                {currentText.login}
              </Button>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}
